// SPDX-FileCopyrightText: Copyright 2025 New Vector Ltd.
// SPDX-FileCopyrightText: Copyright 2025, 2026 Element Creations Ltd.
//
// SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial

import {
  defineMessage,
  type MessageDescriptor,
  type PrimitiveType,
} from "react-intl";

// Enforce that the error messages are in the `errors.` namespace
interface ErrorMessageDescriptor extends MessageDescriptor {
  id: MessageDescriptor["id"] & `errors.${string}`;
}

/**
 * An abstract localized error, which is used to display errors in the user-selected language.
 *
 * Implementers must define the `localizedMessage` property using the `defineMessage` helper, and may
 * optionally define the `localizedValues` getter, to fill in the values for the message.
 *
 * All errors must use the `errors.*` namesapce
 */
export abstract class LocalizedError extends Error {
  abstract readonly localizedMessage: ErrorMessageDescriptor;
  protected values: Record<string, PrimitiveType> = {};
  get localizedValues(): Readonly<Record<string, PrimitiveType>> {
    return this.values;
  }
}

export class NotLoggedInError extends LocalizedError {
  localizedMessage = defineMessage({
    id: "errors.not_logged_in",
    defaultMessage: "Not logged in",
    description:
      "Error message when a request was sent which was supposed to be authenticated, but turns out that the user is not logged in. This is a rare error, usually because of a race-condition during login or logout.",
  });

  constructor(options?: ErrorOptions) {
    super("Not logged in", options);
  }
}

export class AuthorizationDeniedError extends LocalizedError {
  localizedMessage = defineMessage({
    id: "errors.authorization_denied",
    defaultMessage: "Sign-in was cancelled.",
    description:
      "Error message when the authorization server redirected back with an 'access_denied' error, which is what it sends when the user declined the sign-in or the server refused to grant them access.",
  });

  // The server's description never reaches the user — cancelling is not a
  // failure to explain — but it stays on the non-localized message, which the
  // diagnostic stack retains for troubleshooting.
  constructor(errorDescription?: string, options?: ErrorOptions) {
    super(
      errorDescription
        ? `Sign-in was cancelled: ${errorDescription}`
        : "Sign-in was cancelled",
      options,
    );
  }
}

export class AuthorizationError extends LocalizedError {
  localizedMessage = defineMessage({
    id: "errors.authorization_error",
    defaultMessage: "Sign-in failed: {errorMessage}",
    description:
      "Error message when the authorization server redirected back with an OAuth 2.0 error other than 'access_denied'. 'errorMessage' is the description the server gave, usually in english, or the raw error code when it gave none.",
  });

  constructor(errorMessage: string, options?: ErrorOptions) {
    super(`Sign-in failed: ${errorMessage}`, options);
    this.values = {
      errorMessage,
    };
  }
}

export class HttpStatusError extends LocalizedError {
  localizedMessage = defineMessage({
    id: "errors.http_status",
    defaultMessage:
      "E-Chat request failed with status code {status} {statusText}",
    description:
      "Generic error message when we fail to make a request to the specified URL. Typical values for 'status' are '404' or '500', typical values for 'statusText' are 'Not Found' or 'Internal Server Error'",
  });

  readonly status: number;

  constructor(response: Response, options?: ErrorOptions) {
    super(
      `Request to ${response.url} failed with status code ${response.status} ${response.statusText}`,
      options,
    );
    this.status = response.status;
    this.values = {
      status: response.status,
      statusText: response.statusText,
      url: response.url,
    };
  }
}

export class MatrixStandardError extends LocalizedError {
  localizedMessage = defineMessage({
    id: "errors.matrix_standard",
    defaultMessage: "E-Chat request failed ({errorCode}): {errorMessage}",
    description:
      "Generic error message when Synapse returned a rich error, with an error code (M_FORBIDDEN, etc.) and a human-readable error message, usually in english.",
  });

  constructor(errcode: string, error: string, options?: ErrorOptions) {
    super(
      `Request failed with error code ${errcode}. Additionally, the server gave the following error message: ${error}`,
      options,
    );
    this.values = {
      errorCode: errcode,
      errorMessage: error,
    };
  }
}

export class MasApiError extends LocalizedError {
  localizedMessage = defineMessage({
    id: "errors.mas_api",
    defaultMessage: "E-Chat sign-in service returned an error: {errorMessage}",
    description:
      "Generic error message when Matrix Authentication Service returned a structured error. 'errorMessage' is the list of human-readable error titles it gave, usually in english.",
  });

  constructor(errorMessage: string, options?: ErrorOptions) {
    super(
      `The homeserver's authentication service returned an error: ${errorMessage}`,
      options,
    );
    this.values = {
      errorMessage,
    };
  }
}

export class FetchError extends LocalizedError {
  localizedMessage = defineMessage({
    id: "errors.http_fetch",
    defaultMessage: "Unable to connect to E-Chat. Please try again.",
    description:
      "Generic error message when we fail to make a request to the specified URL",
  });

  constructor(url: string | URL, options?: ErrorOptions) {
    super(`Request to ${url} failed`, options);
    this.values = {
      url: url.toString(),
    };
  }
}

export class FetchJsonDecodingError extends LocalizedError {
  localizedMessage = defineMessage({
    id: "errors.http_fetch_json_decoding",
    defaultMessage:
      "Unable to read the response from E-Chat. Please try again.",
    description:
      "Error message when we fail to decode a JSON response from the specified URL",
  });

  constructor(url: string | URL, options?: ErrorOptions) {
    super(`Failed to decode JSON response from ${url}`, options);
    this.values = {
      url: url.toString(),
    };
  }
}
